<template>
    <form @submit.prevent="signUp" class="authorization-form authorization-form-registration">
        <div class="authorization-input_label" id="createAccount-first_name">
            <PrimeInputText id="email_first_name" v-model="signupForm.first_name"  @focus="togglePlaceholder('email_first_name')" @blur="togglePlaceholder('email_first_name')"  :placeholder="!placeholdersHidden.get('email_first_name') ? 'First Name *' : ''" :class="{
                'authorization-first_name_input': true,
                'invalid-state-input': signupForm.errors.first_name
            }"/>
            <label for="email_first_name">
                <p>First Name *</p>
            </label>
            <div class="input-folder">
                <div class="input-support-text invalid-state" v-if="signupForm.errors.first_name">{{ signupForm.errors.first_name }}</div>
            </div>
        </div>
        <div class="authorization-input_label">
            <PrimeInputText id="email_last_name" v-model="signupForm.last_name" @focus="togglePlaceholder('email_last_name')" @blur="togglePlaceholder('email_last_name')"  :placeholder="!placeholdersHidden.get('email_last_name') ? 'Last Name *' : ''" :class="{
                    'authorization-last_name_input': true,
                    'invalid-state-input': signupForm.errors.last_name
                }"/>            <label for="email_last_name">
                <p>Last Name *</p>
            </label>
            <div class="input-folder">
                <div class="input-support-text invalid-state" v-if="signupForm.errors.last_name">{{ signupForm.errors.last_name }}</div>
            </div>      
        </div>
        <EmailInput v-model="signupForm.email" :errors="signupForm.errors.email" id="email_login_register" placeholder="Email *" />
        <InputMask 
            :aria-invalid="signupForm.errors.phone_number != null"
            :aria-errormessage="signupForm.errors.phone_number != null ? 'guest_phone_logged_in-error' : ''"
            @focus="togglePlaceholder('guest_phone_logged_in')"
            @blur="togglePlaceholder('guest_phone_logged_in')"
            id="guest_phone_logged_in" v-model="signupForm.phone_number" mask="(999) 999-9999" :class="{
                'authorization-guest_phone_input': true,
                'invalid-state-input': signupForm.errors.phone_number != null,
                'flex': true,
            }"
            :placeholder="!placeholdersHidden.get('guest_phone_logged_in') ? 'Phone Number' : ''" 
            fluid 
        />
        <PasswordInput v-model="signupForm.password" :errors="passwordTests" id="email_password_register" placeholder="Password *" />
        <PasswordInput v-model="signupForm.password_confirmation" :errors="signupForm.errors.password_confirmation" id="confirm_password_register" placeholder="Confirm Password *" />
        <p class="paragraph-details">Be the first to know about special discounts & new gear via Rothco-exclusive emails & SMS!</p>
        <div class="flex-row justify-around">
            <Checkbox v-model="signupForm.news_emails" label="Signup for Email" style="margin-right:1rem;" id="emailSignup" />
            <Checkbox v-if="!hideSmsSignup" :disabled="[null, '', undefined].includes(signupForm.phone_number)" v-model="signupForm.sms_signup" label="Signup for SMS" id="smsSignup"/>
        </div>

        <p v-show="signupForm.sms_signup && !hideSmsSignup" class="paragraph-caption toggle-on-check">
            By checking this box, you agree to receive recurring automated marketing text messages (e.g. AI content, cart reminders) from Rothco at the number you provide. Consent not a condition of purchase. We may share info with service providers per our Privacy Policy. Reply HELP for help & STOP to cancel. Msg frequency varies. Msg & data rates may apply. By checking this box, you also agree to our <a href="https://attnl.tv/t/ctj" target="_blank" aria-label="Learn more about messaging terms and conditions">Terms</a> (incl. arbitration) & <a href="https://attnl.tv/p/ctj" aria-label="Learn more about messaging privacy policy" target="_blank">Privacy Policy</a>
        </p>

        <button id="registerBtn" type="submit" class="primary" :disabled="signupForm.processing">Create Account
            <span aria-label="hidden" class="icon">
                <CarretsBlack alt="carrets black" />
            </span>
        </button>
    </form>

</template>

<script setup lang="ts">
    import { ref, computed, watch, inject } from "vue";
    import { useForm } from "@/helpers/composables/useForm";
    import { string, object, boolean } from "yup";
    import InputMask from "primevue/inputmask";
    import EmailInput from "@/components/EmailInput.vue";
    import PasswordInput from "@/components/PasswordInput.vue";
    import CarretsBlack from "@icons/carrets-black.svg";
    import Checkbox from "@/components/Checkbox.vue";
    import { usePage } from "@inertiajs/vue3";

    let route = inject('route');

    const hideSmsSignup = computed(() => {
        return usePage().props.hideSmsSignup === true;
    });

    const signupForm = useForm({
        useInertia: true,
        // inertiaAlias: 'SignUpForm',
        abortEarly: false,
        schema: object({
            first_name: string().required('The first name field is required').default(""),
            last_name: string().required('The last name field is required').default(""),
            email: string().email('Please enter a valid email').required('The email field is required').default(""),
            phone_number: string().optional().default(""),
            password: string()
                .required('The password field is required')
                .test('password-strength', '', (value, ctx) => {
                    if (!value) return true;
                    let messages = [];
                    let weakPasswordMessage;
                    if (!/[A-Z]/.test(value)) messages.push('at least one uppercase letter');
                    if (!/[a-z]/.test(value)) messages.push('at least one lowercase letter');
                    if (!/[0-9]/.test(value)) messages.push('at least one number');
                    if (!/[#?!@$%^&*-]/.test(value)) messages.push('at least one special character (# ? ! @ $  % ^ & * -)');
                    if (/\s/.test(value)) messages.push('no spaces');
                    if (value.length < 8) messages.push('at least 8 characters');
                    if (/password/i.test(value)) weakPasswordMessage = 'Your password is too weak.';
                    if (messages.length || weakPasswordMessage) return ctx.createError({ message: messages.length > 0 ? `Password must contain ${messages.join(', ')}.${weakPasswordMessage ? " " + weakPasswordMessage : ""}` : weakPasswordMessage });
                    return true;
                })
                .default(""),

            password_confirmation: string()
                .required('The confirm password field is required')
                .test('passwords-match', 'Password confirmation does not match', function (value) {
                    return value === this.parent.password;
                }),
            sms_signup: boolean().default(false),
            news_emails: boolean().default(false),
        })

    });

    let passwordTests = computed(()=>{
       let passwordErrors = Object.keys(signupForm.errors).filter((key) => {
           return key.includes('password') && key !== 'password_confirmation';
       }).map((key) => {
           return signupForm.errors[key];
       });
       return passwordErrors.length ? [passwordErrors[0]] : [];
    })

    const accountCreationOptions = computed(()=>{
        if (typeof window === "undefined") {
            return {};
        }
        const urlParams = Object.fromEntries(new URLSearchParams(window.location.search));
        return urlParams;
    })

    const attemptedSubmit = ref(false);
    const signUp = async () => {
        if(signupForm.processing) return;

        await signupForm.validate();
        attemptedSubmit.value = true;
        if(!signupForm.hasErrors){
            signupForm.transform((data)=>{
                    return {
                        ...data,
                        ...(accountCreationOptions.value ?? {}),
                    }
                })
                .post(route("web.create.account"), {
                preserveState: (page) => {
                    return Object.keys(page.props.errors).length > 0;
                },
            });
        }       
    };

    let placeholdersHidden = ref(new Map());
    function togglePlaceholder(id) {
        if(!placeholdersHidden.value.has(id)) {
            placeholdersHidden.value.set(id, true);
        }else {
            placeholdersHidden.value.set(id, !placeholdersHidden.value.get(id));
        }
    }
    

    let hasTypedInField = ref(new Map())

    watch(() =>signupForm.first_name, (value) => {
        if(value.length > 0) {
            hasTypedInField.value.set('first_name', true);
        }
    })

    watch(() =>signupForm.last_name, (value) => {
        if(value.length > 0) {
            hasTypedInField.value.set('last_name', true);
        }
    })

    watch(() =>signupForm.email, (value) => {
        if(value.length > 0) {
            hasTypedInField.value.set('email', true);
        }
    })

    watch(() =>signupForm.password, (value) => {
        if(value.length > 0) {
            hasTypedInField.value.set('password', true);
        }
    })

    watch(() => signupForm.phone_number, (value) => {
        if(value.length > 0) {
            hasTypedInField.value.set('phone_number', true);
        }else{
            signupForm.sms_signup = false;
        }
    })

    watch(hideSmsSignup, (hidden) => {
        if (hidden) {
            signupForm.sms_signup = false;
        }
    }, { immediate: true })

</script>
<style></style>
